import React, { useEffect, useState, useRef } from 'react'
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { Doughnut } from 'react-chartjs-2';

import CustomButton from '../../CustomButton'
import { getProfileUpdateData, getProfileUpdateStates } from "../../../action/CompaniesActions";
import { tableAdjustableColOptions } from "../../helpers/table";
import momentUtcToUserTz from "../../helpers/momentUtcToUserTz";

const UpdateProgressModal = (props) => {
	const tableRef = useRef();
	const { t } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.fleetConfigurationContainer.updateProgressModal' });
	const { showProgressModal, setShowProgressModal, selectedProfile } = props;
	const defaultStatesValues = {
		applying_update: null,
		done: null,
		done_with_warnings: null,
		pending_update: null,
		total_progress: null,
	};

	const [statsData, setStatsData] = useState(defaultStatesValues);
	const [tableData, setTableData] = useState([]);

	const renderStatesOrder = [
		'pending_update',
		'applying_update',
		'done',
		'done_with_warnings'
	];

	useEffect(() => {
		getUpdateDate();

		const intervalId = setInterval(() => {
			getUpdateDate();
		}, 10000);

		return () => clearInterval(intervalId);
	}, []);

	const onHidePopUp = () => {
		setShowProgressModal(false);
	}

	const getUpdateDate = () => {
		Promise.all([
			getProfileUpdateStates({
				profileId: selectedProfile.id,
			}),
			getProfileUpdateData({
				profileId: selectedProfile.id,
			})
		]).then(res => {
			const statesRes = res[0].data.response;
			const { receiving_update, ...rest } = statesRes;
			const updateStats = Object.keys(rest).length > 0 ? rest : {...defaultStatesValues};
			const updateData = res[1].data.response || [];
			setStatsData(updateStats);
			setTableData(updateData);
		}).catch(err => {
			console.log("Error getting profile update data", err);
		})
	}

	const handleExportToCSV = () => {
		if (tableRef.current) {
			tableRef.current.handleExportCSV();
		}
	};

	const timestampFormatter = (cell) => {
		return cell ? momentUtcToUserTz(cell).format('M/DD/YYYY hh:mm:ss A') : '';
	}

	const isPropertyValid = (prop) => prop || prop === 0 || prop === '0';

	return (
		<Modal
			show={showProgressModal}
			onHide={onHidePopUp}
			size="xl"
			className="update-progress-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title id="contained-modal-title-lg">{selectedProfile.name} - {t('updateProgress')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="export-container">
					<CustomButton
						variant="primary"
						size="sm"
						onClick={handleExportToCSV}
					>
						{t('exportBtn')}
					</CustomButton>
				</div>
				<div className="stats-wrapper">
					<div className="progress-container">
						{t('updateProgressLabel')}
						<div className="chart-container">
							<Doughnut
								data={{
									datasets: [
										{
											data: [
												100 - statsData.total_progress,
												statsData.total_progress,
											],
											backgroundColor: [
												'transparent',
												'rgba(41, 169, 224, 1)',
											],
											hoverBackgroundColor: [
												'transparent',
												'rgba(41, 169, 224, 1)',
											],
											borderWidth: 0,
											hoverBorderWidth: 0,
										}
									]
								}}
								options={{
									responsive: true,
									maintainAspectRatio: false,
									tooltips: {
										enabled: false,
									},
									legend: {
										display: false,
									},
									cutoutPercentage: 60,
								}}

							/>
							<div className="chart-value-label">
								{`${isPropertyValid(statsData.total_progress) ? statsData.total_progress + '%' : 'N/A'}`}
							</div>
						</div>
					</div>
					<div className="states-container">
						% {t('statesLabel')}
						<div className="states-list">
							{
								Object.entries(statsData)
									.filter(([key]) => key !== 'total_progress')
									.sort(([a], [b]) => renderStatesOrder.indexOf(a) - renderStatesOrder.indexOf(b))
									.map(([key, value]) => (
										<div className="state-row" key={key}>
											<span>{t(key) || 'N/A'}</span>
											<span>
												{`${isPropertyValid(value) ? value + '%' : 'N/A'}`}
											</span>
										</div>
									))
							}
						</div>
					</div>
				</div>
				<div className="table-container">
					<BootstrapTable
						ref={tableRef}
						data={tableData}
						{...tableAdjustableColOptions}
						csvFileName={`${selectedProfile.name}_${t('csvFileName')}.csv`}
					>
						<TableHeaderColumn
							dataField="camera_name"
							dataSort
							csvHeader={t('cameraName')}
						>
							{t('cameraName')}
						</TableHeaderColumn>
						<TableHeaderColumn
							dataField="current_state"
							dataSort
							csvHeader={t('currentState')}
						>
							{t('currentState')}
						</TableHeaderColumn>
						<TableHeaderColumn
							dataField="group"
							dataSort
							csvHeader={t('group')}
						>
							{t('group')}
						</TableHeaderColumn>
						<TableHeaderColumn
							isKey
							dataField="received_update_at"
							dataSort
							dataFormat={timestampFormatter}
							csvFormat={timestampFormatter}
							csvHeader={t('receivedAt')}
						>
							{t('receivedAt')}
						</TableHeaderColumn>
						<TableHeaderColumn
							dataField="applied_update_at"
							dataSort
							dataFormat={timestampFormatter}
							csvFormat={timestampFormatter}
							csvHeader={t('appliedAt')}
						>
							{t('appliedAt')}
						</TableHeaderColumn>
						<TableHeaderColumn
							dataField="warnings"
							dataSort
							csvHeader={t('warnings')}
						>
							{t('warnings')}
						</TableHeaderColumn>
					</BootstrapTable>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<CustomButton
					variant="primary"
					size="sm"
					onClick={onHidePopUp}
				>
					{t('closeBtn')}
				</CustomButton>
			</Modal.Footer>
		</Modal>
	)
}

export default UpdateProgressModal;
